import React, { useState, useEffect, useMemo, useRef } from 'react';
import { identity, pick } from 'ramda';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { LocalPopup } from 'js/components/common/Popup';
import { JumioPopupUploader } from './components/JumioPopupUploader';
import { useOnClose } from 'js/components/common/PopupManager';
import { JumioPopupUpdateInformation } from 'js/components/system/Page/buzz/components/JumioPopup/components/JumioPopupUpdateInformation/JumioPopupUpdateInformation';

import { useTranslations } from 'js/hooks/i18n';
import { getURLParam } from 'js/utils/url.util';
import { useUserSelector } from 'js/store/hooks';
import { htcmdService } from 'js/services/htcmd.service';
import { useLogout } from '@interlayer/js/hooks/auth/hooks/useLogout';
import { scrollService } from 'js/services/scroll.service';

import { useGtmEvent } from 'js/app/hooks/analiticsSubscribe';
import { setJumioStatus } from 'js/hooks/user/store/buzz/actions';
import { useNavigateToHome } from 'js/app/hooks/navigate/useNavigateToHome';
import { useRegistrationSelector } from 'js/app/store/hooks/useRegistrationSelector';
import { PLAYER_TAG_IDV_CHECKED, PLAYER_TAG_IDV_UPLOADED } from 'js/components/system/Page/buzz/constants';
import { useSetUpdateUserDetailsStatus } from 'js/components/system/Page/buzz/components/JumioPopup/hooks/useSetUpdateUserDetailsStatus';

import style from './JumioPopup.scss';
import { useAfterLoginAction } from '@interlayer/js/hooks/auth/hooks/useAfterLoginAction';

export const JumioPopup = ({ initialJumioState, isOpenByUrl }) => {
    const logout = useLogout();
    const gtmEvent = useGtmEvent();
    const dispatch = useDispatch();
    const navigateToHome = useNavigateToHome();
    const translate = useTranslations('playtech.mobile.system.id-verification.');
    const setUpdateUserDetailsStatus = useSetUpdateUserDetailsStatus();

    const closeManagedPopup = useOnClose();
    const [isShowLogout, setIsShowLogout] = useState(false);
    const [isHideClose, setIsHideClose] = useState(false);
    const isAlreadyShowingLoading = useRef(false);
    const [isVerificationPassed, setVerificationPassed] = useState(false);
    const { justRegistered, jumioState, allPlayerTags: playerTags } = useUserSelector(pick(['justRegistered', 'jumioState', 'allPlayerTags']));

    const { search } = useLocation();
    const isAfterRegistration = getURLParam(search, 'isAfterRegistration');
    const { isRegistrationInProcess } = useRegistrationSelector();
    const isGracePeriod = jumioState === 'gracePeriod';
    const afterLoginAction = useAfterLoginAction();

    const { isUploadedDoc, isCheckedDoc } = useMemo(() => {
        if (!playerTags) {
            return {};
        }

        return {
            isCheckedDoc: playerTags[PLAYER_TAG_IDV_CHECKED],
            isUploadedDoc: playerTags[PLAYER_TAG_IDV_UPLOADED] && !playerTags[PLAYER_TAG_IDV_CHECKED]
        };
    }, [playerTags]);

    useEffect(() => {
        if (initialJumioState) {
            dispatch(setJumioStatus(initialJumioState));
        }
    }, [dispatch, initialJumioState]);

    useEffect(() => {
        scrollService.scrollTo(0);
        setUpdateUserDetailsStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [title, content] = useMemo(() => {
        if (isCheckedDoc) {
            return ['failedDoc.title', 'failedDoc.content'];
        }

        if (isUploadedDoc) {
            return ['uploaded.title', 'uploaded.content'];
        }

        return justRegistered || isAfterRegistration
            ? ['need-more-info.title', 'need-more-info.content']
            : ['welcome-back.title', 'welcome-back.content'];
    }, [isAfterRegistration, isCheckedDoc, isUploadedDoc, justRegistered]);

    if (['done', 'logout'].includes(jumioState) || isRegistrationInProcess || !playerTags) {
        return null;
    }

    const startingButtons = [{
        onClick: () => {
            if (isUploadedDoc) {
                logout({
                    afterLogout:closeForNative
                });
                if (!htcmdService.isEnabled) {
                    navigateToHome();
                }
                return;
            }

            dispatch(setJumioStatus('iframe'));
        },
        text: translate(isUploadedDoc ? 'uploaded.btn' : 'welcome-back.btn'),
        view: 'primary',
        size: 'medium',
        className: `${style.cta} ${htcmdService.isEnabled && isUploadedDoc ? 'hidden' : ''}`
    }];

    const closeForNative = () => {
        if (!htcmdService.isEnabled) {
            return;
        }

        htcmdService.sendMessage('close');
    };

    const closeJumioIframePopup = ({ isOnlyLogout } = {}) => {
        if (isVerificationPassed) {
            dispatch(setJumioStatus('done'));
            closeForNative();
            afterLoginAction();
            return;
        }

        const afterLogout = () => {
            gtmEvent('IDV abandonded');
            closeForNative();
        };

        if (isOpenByUrl && !isOnlyLogout) {
            logout({ afterLogout });
            if (!htcmdService.isEnabled) {
                navigateToHome();
            }
            return;
        }

        const getBackToJumio = () => {
            dispatch(setJumioStatus('iframe'));
        };

        logout({
            onDecline: getBackToJumio,
            onlyLogout: isOnlyLogout,
            onClose: getBackToJumio,
            afterLogout
        });
        dispatch(setJumioStatus('logout'));
    };

    const closePopup = () => {
        dispatch(setJumioStatus(''));
        closeManagedPopup();
    };

    const closeUpdateInformationPopup = ({ afterLogout }) => {
        if (!isShowLogout) {
            setIsShowLogout(true);
            return;
        }

        const afterLogoutAction = () => {
            afterLogout && afterLogout();
            closeForNative();
        };

        logout({ isQuiet: true, afterLogout: afterLogoutAction });
    };

    if (jumioState === 'iframe' || jumioState === 'gracePeriod') {
        return (
            <LocalPopup
                isModal={htcmdService.isEnabled}
                fullscreen={htcmdService.isEnabled}
                onClose={isGracePeriod ? closePopup : closeJumioIframePopup}
                onOverlayClick={isGracePeriod ? closePopup : closeJumioIframePopup}
                className={style.popup}
                classNames={{
                    container: style.container
                }}
                title={!isGracePeriod && translate('identity-check')}
            >
                <JumioPopupUploader
                    setVerificationPassed={setVerificationPassed}
                    onClose={closeJumioIframePopup}
                />
            </LocalPopup>
        );
    }

    if (jumioState === 'updateUserDetails' && justRegistered) {
        return (
            <LocalPopup
                onClose={closeUpdateInformationPopup}
                onOverlayClick={closeUpdateInformationPopup}
                fullscreen
                className={style.updateInformationPopup}
                classNames={{
                    title: style.title,
                    container: style.container
                }}
                title={translate('failed.title')}
                isModal={htcmdService.isEnabled || isHideClose}
            >
                <JumioPopupUpdateInformation
                    closeForNative={closeForNative}
                    isShowLogout={isShowLogout}
                    setIsShowLogout={setIsShowLogout}
                    setIsHideClose={setIsHideClose}
                    isAlreadyShowingLoading={isAlreadyShowingLoading.current}
                />
            </LocalPopup>
        );
    }

    return (
        <LocalPopup
            fullscreen={htcmdService.isEnabled}
            buttons={startingButtons}
            onOverlayClick={identity}
            className={style.popup}
            classNames={{
                container: style.container
            }}
            title={translate(title)}
            isModal
        >
            {translate(content)}
        </LocalPopup>
    );
};
